import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DocsHeader from "../components/docs/DocsHeader";
import DocsSidebar from "../components/docs/DocsSidebar";
import MainContent from "../components/docs/MainContent";
import { Copy, Check } from "lucide-react";

const contentVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 24 },
};

const keywordToSection = {
  introduction: "introduction",
  "send essages": "messages",
  chats: "get-chats",
  groups: "get-all-groups",
  "group details": "get-group-details",
  api: "api",
  "getting started": "getting-started",
};

export default function Docs() {
  const [activeSection, setActiveSection] = useState("getting-started");
  const [expandedSections, setExpandedSections] = useState(["getting-started"]);
  const [copiedCode, setCopiedCode] = useState("");
  const [activeLang, setActiveLang] = useState("Python");
  const [searchText, setSearchText] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSearchChange = (e) => {
    const text = e.target.value.toLowerCase().trim();
    setSearchText(text);

    if (text === "") {
      setActiveSection("introduction");
      setExpandedSections(["getting-started"]);
      return;
    }

    const tokens = text.split(/\s+/);

    const matches = Object.entries(keywordToSection).filter(([key]) => {
      const normalizedKey = key.toLowerCase();
      return tokens.every((token) => normalizedKey.includes(token));
    });

    if (matches.length > 0) {
      const bestMatch = matches.reduce((prev, curr) =>
        curr[0].length < prev[0].length ? curr : prev
      );

      setActiveSection(bestMatch[1]);

      if (
        ["bulk-messages", "messages", "get-chats", "get-all-groups", "get-group-details"].includes(
          bestMatch[1]
        )
      ) {
        if (!expandedSections.includes("api")) {
          setExpandedSections((prev) => [...prev, "api"]);
        }
      }
      if (["introduction", "quick-start"].includes(bestMatch[1])) {
        if (!expandedSections.includes("getting-started")) {
          setExpandedSections((prev) => [...prev, "getting-started"]);
        }
      }
    } else {
      setActiveSection("default");
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(""), 2000);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  };

  const CopyButton = ({ code, id }) => (
    <button
      onClick={() => copyToClipboard(code, id)}
      className="inline-flex items-center px-2 py-1 rounded bg-gray-100 border border-gray-200 hover:bg-gray-200 transition text-gray-700"
      aria-label="Copy code"
      type="button"
    >
      {copiedCode === id ? (
        <motion.span initial={{ scale: 0.8 }} animate={{ scale: 1.2 }} transition={{ type: "spring", duration: 0.2 }}>
          <Check size={16} className="text-[#01CC54]" />
        </motion.span>
      ) : (
        <Copy size={16} className="text-gray-600" />
      )}
    </button>
  );

  return (
    <div className="flex flex-col h-screen bg-white text-gray-900 md:pl-64">
      <DocsHeader
        searchText={searchText}
        onSearchChange={handleSearchChange}
        onSidebarOpen={() => setSidebarOpen(true)}
      />

      <DocsSidebar
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      <main className="flex-1 overflow-y-auto p-2 md:p-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={contentVariants}
            transition={{ duration: 0.3, type: "tween" }}
            className="w-full"
          >
            <MainContent
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              copiedCode={copiedCode}
              copyToClipboard={copyToClipboard}
              activeLang={activeLang}
              setActiveLang={setActiveLang}
              CopyButton={CopyButton}
            />
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
